import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQuery } from "@/graphql";
import {
  CHANGE_PASSWORD_MUTATION,
  CREATE_SUPPORT_MUTATION,
  UPDATE_USER_BY_ID,
  UPDATE_COMPANY_INFO,
  GET_COMPANY_INFO_BY_ID,
} from "@/graphql/account-settings-request";
import { setCompanyInfo } from "./setting-slice";
import { signedUrlApi } from "../signed-url";

export const settingApi = createApi({
  reducerPath: "api/settings",
  baseQuery,
  tagTypes: ["CompanyInfo"],
  endpoints: (builder) => ({
    changePassword: builder.mutation<
      string,
      { previousPassword: string; newPassword: string }
    >({
      query: ({ previousPassword, newPassword }) => ({
        document: CHANGE_PASSWORD_MUTATION,
        variables: { previousPassword, newPassword },
      }),
      onQueryStarted: async (_, { queryFulfilled }) => {
        try {
          await queryFulfilled;
        } catch (error) {}
      },
      transformResponse: (response) => response.updateUserPassword.data,
    }),
    createSupport: builder.mutation<
      string,
      { support: { message: string; subject: string } }
    >({
      query: ({ support }) => ({
        document: CREATE_SUPPORT_MUTATION,
        variables: { support },
      }),
      onQueryStarted: async (_, { queryFulfilled }) => {
        try {
          await queryFulfilled;
        } catch (error) {}
      },
      transformResponse: (response) => response.createSupport.data,
    }),
    updateUserInfo: builder.mutation<
      boolean,
      {
        userId: string;
        userData: {
          firstName: string;
          lastName: string;
          profileImage?: string;
        };
      }
    >({
      query: ({ userData, userId }) => ({
        document: UPDATE_USER_BY_ID,
        variables: { userData, userId },
      }),
      transformResponse: (response) => response.updateUserById.data,
    }),
    updateCompanyInfo: builder.mutation<
      boolean,
      {
        tenantData: {
          companyName: string;
          businessAddress: string;
          companyLogo?: string;
        };
        tenantId?: string;
      }
    >({
      query: ({ tenantData, tenantId }) => ({
        document: UPDATE_COMPANY_INFO,
        variables: { tenantData, tenantId },
      }),
      transformResponse: (response) => response.updateCompanyInfo.data,
      invalidatesTags: ["CompanyInfo"],
    }),
    getCompanyInfo: builder.query<
      {
        companyName: string;
        companyEmail: string;
        businessAddress: string;
        companyLogo: string;
      },
      void
    >({
      query: () => ({
        document: GET_COMPANY_INFO_BY_ID,
      }),
      transformResponse: (response) => response.getTenantById.data,
      providesTags: ["CompanyInfo"],
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;

          let companyLogoUrl = "";

          if (data.companyLogo) {
            const { signedUrl } = await dispatch(
              signedUrlApi.endpoints.getAccessSignedUrl.initiate({
                fileName: data.companyLogo,
              })
            ).unwrap();

            companyLogoUrl = signedUrl;
          }

          dispatch(setCompanyInfo({ ...data, companyLogo: companyLogoUrl }));
        } catch (error) {}
      },
    }),
  }),
});

export const {
  useChangePasswordMutation,
  useCreateSupportMutation,
  useUpdateUserInfoMutation,
  useUpdateCompanyInfoMutation,
  useGetCompanyInfoQuery,
} = settingApi;
